import { useState, useEffect } from 'react'
import { FloppyDiskIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react'
import { ScoopButton, ScoopText, ScoopSpinner, ScoopRow, ScoopCenter } from '../../../../../ui'
import { DefinitionTable } from '../shared'
import styles from './DefinitionsForm.module.scss'

/**
 * DefinitionsForm - Form for editing definitions.json configuration
 *
 * @param {Object} data - Current definitions content
 * @param {Function} onSave - Callback when saving (receives updated data)
 * @param {boolean} isSaving - Whether save is in progress
 * @param {boolean} isLoading - Whether data is loading
 */
export const DefinitionsForm = ({ data, onSave, isSaving, isLoading, onDirtyStateChange }) => {
    const [definitions, setDefinitions] = useState([])
    const [hasChanges, setHasChanges] = useState(false)

    // Initialize form data when data prop changes
    useEffect(() => {
        if (data) {
            // Convert definitions object to array for editing
            // Definitions are at the root level, filter out _ prefixed metadata keys
            // Sort alphabetically by name
            const definitionsArray = Object.entries(data)
                .filter(([key]) => !key.startsWith('_'))
                .map(([name, def]) => ({
                    name,
                    description: def.description || '',
                    formula: typeof def.formula === 'string' ? def.formula : '',
                    format: def.format || '',
                    type: def.metric_type || def.type || 'balance',
                }))
                .sort((a, b) => a.name.localeCompare(b.name))

            setDefinitions(definitionsArray)
            setHasChanges(false)
        }
    }, [data])

    useEffect(() => {
        onDirtyStateChange?.(hasChanges)
    }, [hasChanges, onDirtyStateChange])

    const handleDefinitionsChange = (newDefinitions) => {
        setDefinitions(newDefinitions)
        setHasChanges(true)
    }

    const handleSave = () => {
        // Preserve metadata keys (starting with _) from original data
        const metadataKeys = Object.entries(data || {})
            .filter(([key]) => key.startsWith('_'))
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

        // Convert definitions array back to object format at root level
        const definitionsObject = {}
        definitions.forEach((def) => {
            if (def.name?.trim()) {
                definitionsObject[def.name.trim()] = {
                    description: def.description,
                    formula: def.formula,
                    format: def.format,
                    metric_type: def.type,
                }
            }
        })

        // Combine metadata and definitions at root level
        const updatedData = {
            ...metadataKeys,
            ...definitionsObject,
        }

        onSave(updatedData)
        setHasChanges(false)
    }

    const handleReset = () => {
        if (data) {
            // Same logic as initial load - filter out _ prefixed metadata, sort alphabetically
            const definitionsArray = Object.entries(data)
                .filter(([key]) => !key.startsWith('_'))
                .map(([name, def]) => ({
                    name,
                    description: def.description || '',
                    formula: typeof def.formula === 'string' ? def.formula : '',
                    format: def.format || '',
                    type: def.metric_type || def.type || 'balance',
                }))
                .sort((a, b) => a.name.localeCompare(b.name))

            setDefinitions(definitionsArray)
            setHasChanges(false)
        }
    }

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loading}>
                <ScoopSpinner size={32} />
                <ScoopText>Loading definitions...</ScoopText>
            </ScoopCenter>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <ScoopText as="h2" className={styles.title}>
                        Business Terms
                    </ScoopText>
                    <ScoopText className={styles.description}>
                        Define business terms and calculated metrics. These definitions are
                        available across all investigation contexts and help the AI understand your
                        business vocabulary.
                    </ScoopText>
                </div>
                <ScoopRow className={styles.actions}>
                    {hasChanges && (
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={handleReset}
                            disabled={isSaving}
                            leftSection={<ArrowCounterClockwiseIcon size={16} />}
                        >
                            Reset
                        </ScoopButton>
                    )}
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        loading={isSaving}
                        leftSection={<FloppyDiskIcon size={16} />}
                    >
                        Save Changes
                    </ScoopButton>
                </ScoopRow>
            </div>

            <div className={styles.content}>
                <DefinitionTable
                    definitions={definitions}
                    onChange={handleDefinitionsChange}
                    disabled={isSaving}
                    emptyMessage="No business terms defined yet. Click 'Add Term' to create your first definition."
                />
            </div>
        </div>
    )
}
